import React, { useEffect, useState } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../hooks/useConfig';
import { apiService } from '../services/api';
import { sanitizeLoginErrorMessage, sanitizeOAuthLoginErrorMessage } from '../utils/authLogin';
import { LoginBackground } from '../components/auth/LoginBackground';
import { isTenantHost, slugFromHostname } from '../utils/tenantSlug';
import { isDisplayableImageUrl } from '../utils/assignmentSubmission';

const ZOHO_STATE_KEY = 'rust-school-zoho-oauth-state';
const GOOGLE_STATE_KEY = 'rust-school-google-oauth-state';
const LOGIN_VISIT_RECORDED_KEY = 'rust-school-login-visit-recorded';
const PLATFORM_TITLE = 'SkoolFix';
const PLATFORM_TAGLINE = 'The campus OS for modern Indian schools';

export function LoginPage() {
  const { login, isAuthenticated, loading } = useAuth();
  const { loading: configLoading } = useConfig();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [zohoLoading, setZohoLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [zohoOAuthEnabled, setZohoOAuthEnabled] = useState(false);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState(false);
  const [schoolName, setSchoolName] = useState<string | null>(null);
  const [logoSrc, setLogoSrc] = useState('');
  const [logoBroken, setLogoBroken] = useState(false);

  const platformHost = !isTenantHost();
  const oauthBusy = zohoLoading || googleLoading;
  const displayTitle = schoolName?.trim() || PLATFORM_TITLE;
  const showSchoolBranding = Boolean(schoolName?.trim());

  useEffect(() => {
    let cancelled = false;
    apiService
      .getPublicConfig()
      .then((res) => {
        if (cancelled) return;
        // Zoho stays tenant-portal only; Google can appear on any login host when enabled.
        setZohoOAuthEnabled(!platformHost && Boolean(res.data?.zoho_oauth_enabled));
        setGoogleOAuthEnabled(Boolean(res.data?.google_oauth_enabled));
      })
      .catch(() => {
        if (!cancelled) {
          setZohoOAuthEnabled(false);
          setGoogleOAuthEnabled(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [platformHost]);

  // Record visits for Super Admin analytics only — never show a public counter on login.
  useEffect(() => {
    const alreadyRecorded =
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(LOGIN_VISIT_RECORDED_KEY) === '1';
    if (alreadyRecorded) return;

    const path =
      typeof window !== 'undefined' ? window.location.pathname || '/login' : '/login';
    const host = typeof window !== 'undefined' ? window.location.host || '' : '';
    const userAgent =
      typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';

    apiService
      .recordLoginPageVisit({
        path,
        host: host || undefined,
        user_agent: userAgent || undefined,
      })
      .then(() => {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(LOGIN_VISIT_RECORDED_KEY, '1');
        }
      })
      .catch(() => {
        // Ignore — visit tracking must not affect login UX.
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const slug =
      typeof window !== 'undefined' ? slugFromHostname(window.location.hostname) : null;
    if (!slug) {
      setSchoolName(null);
      setLogoSrc('');
      setLogoBroken(false);
      return;
    }

    apiService
      .resolveTenant({ slug })
      .then((res) => {
        if (cancelled) return;
        const name = res.data?.name?.trim() || null;
        const displayUrl = (res.data?.logo_display_url || res.data?.logo_url || '').trim();
        setSchoolName(name);
        setLogoSrc(displayUrl && isDisplayableImageUrl(displayUrl) ? displayUrl : '');
        setLogoBroken(false);
      })
      .catch(() => {
        if (!cancelled) {
          setSchoolName(null);
          setLogoSrc('');
          setLogoBroken(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const postLoginPath = (() => {
    if (typeof window === 'undefined') return '/';
    const next = new URLSearchParams(window.location.search).get('next')?.trim() || '';
    if (next.startsWith('/') && !next.startsWith('//')) return next;
    return '/';
  })();

  if (!loading && !configLoading && isAuthenticated) {
    return <Redirect to={postLoginPath} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      history.replace(postLoginPath);
    } catch (err) {
      setError(sanitizeLoginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleZohoSignIn = async () => {
    setError(null);
    setZohoLoading(true);
    try {
      const res = await apiService.getZohoOAuthAuthorizeUrl();
      const authorizeUrl = res.data?.authorize_url?.trim();
      const state = res.data?.state?.trim();
      if (!authorizeUrl || !state) {
        throw new Error('Zoho sign-in is not configured');
      }
      sessionStorage.setItem(ZOHO_STATE_KEY, state);
      window.location.assign(authorizeUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zoho sign-in failed');
      setZohoLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const res = await apiService.getGoogleOAuthAuthorizeUrl();
      const authorizeUrl = res.data?.authorize_url?.trim();
      const state = res.data?.state?.trim();
      if (!authorizeUrl || !state) {
        throw new Error('Google sign-in is not configured');
      }
      sessionStorage.setItem(GOOGLE_STATE_KEY, state);
      window.location.assign(authorizeUrl);
    } catch (err) {
      setError(sanitizeOAuthLoginErrorMessage(err, 'Google'));
      setGoogleLoading(false);
    }
  };

  return (
    <div className="login-page">
      <LoginBackground />
      <div className="login-page__content flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="relative inline-flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-primary-400/30 bg-gradient-to-br from-primary-500/15 to-sky-400/10 mb-4 shadow-lg shadow-sky-900/20">
              {logoSrc && !logoBroken ? (
                <img
                  src={logoSrc}
                  alt=""
                  className="h-full w-full object-contain bg-white/95 p-1.5"
                  onError={() => setLogoBroken(true)}
                />
              ) : (
                <>
                  <i className="fas fa-graduation-cap text-primary-300 text-2xl" aria-hidden />
                  <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-violet-500/25 border border-violet-300/40">
                    <i className="fas fa-wand-magic-sparkles text-violet-200 text-[10px]" aria-hidden />
                  </span>
                </>
              )}
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-primary-300/90 font-medium mb-1">
              {platformHost ? 'Platform Admin' : 'Institution Portal'}
            </p>
            <h1 className="font-display text-2xl font-bold text-royal-ivory">{displayTitle}</h1>
            <p className="text-sm text-primary-200/90 mt-2">
              {showSchoolBranding
                ? 'Sign in to continue'
                : platformHost
                  ? 'Sign in with platform super-admin credentials'
                  : PLATFORM_TAGLINE}
            </p>
            {platformHost ? (
              <p className="text-sm text-primary-200/70 mt-1">
                School staff and students must use their campus portal URL.
              </p>
            ) : (
              !showSchoolBranding && (
                <p className="text-sm text-primary-200/70 mt-1">Sign in to continue</p>
              )
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="royal-card login-page__card p-8 shadow-royal-lg border border-primary-500/20"
          >
            <div className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-secondary-700 mb-1">
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  required
                  className="royal-input w-full px-3 py-2.5 text-sm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="login-password" className="block text-sm font-medium text-secondary-700">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="royal-input w-full px-3 py-2.5 text-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <p className="mt-4 text-sm text-red-600" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || oauthBusy}
              className="mt-6 w-full royal-btn-primary py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>

            {googleOAuthEnabled && (
              <>
                <button
                  type="button"
                  disabled={submitting || oauthBusy}
                  onClick={handleGoogleSignIn}
                  className="mt-3 w-full py-2.5 rounded-lg text-sm font-medium border border-secondary-300 bg-white text-secondary-800 hover:bg-secondary-50 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden>
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {googleLoading ? 'Redirecting to Google…' : 'Sign in with Google'}
                </button>
                <p className="mt-2 text-xs text-secondary-500 text-center">
                  Only accounts already registered by your school can sign in with Google.
                </p>
              </>
            )}

            {!platformHost && zohoOAuthEnabled && (
              <button
                type="button"
                disabled={submitting || oauthBusy}
                onClick={handleZohoSignIn}
                className="mt-3 w-full py-2.5 rounded-lg text-sm font-medium border border-secondary-300 bg-white text-secondary-800 hover:bg-secondary-50 disabled:opacity-50"
              >
                {zohoLoading ? 'Redirecting to Zoho…' : 'Sign in with Zoho'}
              </button>
            )}
          </form>
          <p className="mt-3 text-center text-xs text-primary-200/70">
            <Link to="/careers" className="text-primary-300 hover:text-primary-200 font-medium">
              Careers
            </Link>
            {platformHost && (
              <>
                {' · '}
                <Link to="/" className="text-primary-300 hover:text-primary-200 font-medium">
                  Back to SkoolFix
                </Link>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export { ZOHO_STATE_KEY, GOOGLE_STATE_KEY };
